'use strict';
import { guuid, isNeString, isNumber, isObject, isString } from '@aretheyhappy/utils';
import I18N from '../I18N';
var NOTIFICATION_POSITION;
(function (NOTIFICATION_POSITION) {
    NOTIFICATION_POSITION["TOP"] = "top";
    NOTIFICATION_POSITION["BOTTOM"] = "bottom";
})(NOTIFICATION_POSITION || (NOTIFICATION_POSITION = {}));
var NOTIFICATION_TYPE;
(function (NOTIFICATION_TYPE) {
    NOTIFICATION_TYPE["DEFAULT"] = "default";
    NOTIFICATION_TYPE["ERROR"] = "error";
    NOTIFICATION_TYPE["WARNING"] = "warning";
    NOTIFICATION_TYPE["SUCCESS"] = "success";
})(NOTIFICATION_TYPE || (NOTIFICATION_TYPE = {}));
/**
 * Definition for a notification to be displayed.
 */
class NotificationConfig {
    constructor(body = '') {
        // Unique id for notification, will be added to id tag in html
        this.id = `notification-${guuid().replace(/-/g, '')}`;
        // Type of notification shown
        this.type = NOTIFICATION_TYPE.DEFAULT;
        // Icon for notification, will default to the icon used for the type
        this.icon = false;
        // Optional title for notification
        this.title = false;
        // Main body text for notification
        this.body = false;
        // Time to show the notification for
        this.hide_after = 5000;
        // Position of the notification
        this.position = NOTIFICATION_POSITION.TOP;
        // Reference to hide timeout for notification (used internally)
        this.timeout = undefined;
        // If the notification has already been removed (used internally)
        this.removed = false;
        this.body = body;
    }
    /**
     * Set the type of notification, effects style and icon
     * @param type
     * @returns
     */
    setType(type) { this.type = type; return this; }
    setIcon(icon) { this.icon = icon; return this; }
    /**
     * Set the title, will be ran through I18N
     * @param title
     * @returns
     */
    setTitle(title) { this.title = title; return this; }
    /**
     * Set the body, will be ran through I18N
     * @param title
     * @returns
     */
    setBody(body) { this.body = body; return this; }
    setHideTime(hide) { this.hide_after = hide; return this; }
    setPosition(position) { this.position = position; return this; }
    asError() { this.type = NOTIFICATION_TYPE.ERROR; return this; }
    asSuccess() { this.type = NOTIFICATION_TYPE.SUCCESS; return this; }
    asWarning() { this.type = NOTIFICATION_TYPE.WARNING; return this; }
    getIcon() {
        if (isNeString(this.icon))
            return this.icon;
        switch (this.type) {
            case NOTIFICATION_TYPE.SUCCESS:
                return 'checkmark-circle';
            case NOTIFICATION_TYPE.WARNING:
                return 'warning';
            case NOTIFICATION_TYPE.ERROR:
                return 'prohibited';
            case NOTIFICATION_TYPE.DEFAULT:
            default:
                return 'alarm';
        }
    }
    /**
     * Remove notification from the screen
     * @returns
     */
    remove(parent) {
        // Remove the timeout if one is set
        if (this.timeout !== undefined)
            clearTimeout(this.timeout);
        // If the notification is already removed, do nothing
        if (this.removed)
            return;
        this.removed = true;
        //  Add out animation class
        const cell = parent.querySelectorAll(`#${this.id}`)[0];
        if (cell === null)
            return;
        cell.classList.add('el--anim-out');
        //  Remove after 200 ms
        setTimeout(() => {
            if (cell.parentNode !== null)
                cell.parentNode.removeChild(cell);
        }, 200);
    }
}
/**
 * Translate the sent val
 * @param val
 * @returns
 */
const serialize = (val = false) => {
    let serialized = false;
    if (val === false)
        return false;
    if (isString(val)) {
        serialized = I18N.go(val);
    }
    else if (isObject(val)) {
        serialized = I18N.go(val.lbl || '', val.params || {});
    }
    return isNeString(serialized) ? serialized.trim() : false;
};
/**
 * Global service that will display notifications on the page.
 */
class UINotificationService {
    static getInstance() {
        if (!UINotificationService.instance) {
            UINotificationService.instance = new UINotificationService();
        }
        return UINotificationService.instance;
    }
    constructor() {
        this._installed = false;
    }
    /**
     * Setup the document body to accept notifications.
     */
    install() {
        if (this._installed === true)
            return;
        this._installed = true;
        this.parent = {
            [NOTIFICATION_POSITION.BOTTOM]: document.createElement('aside'),
            [NOTIFICATION_POSITION.TOP]: document.createElement('aside'),
        };
        this.parent[NOTIFICATION_POSITION.BOTTOM].classList.add('f', 'fv', 'el-notification-wrapper', 'el--va-b');
        document.body.appendChild(this.parent[NOTIFICATION_POSITION.BOTTOM]);
        this.parent[NOTIFICATION_POSITION.TOP].classList.add('f', 'fv', 'el-notification-wrapper', 'el--va-t');
        document.body.appendChild(this.parent[NOTIFICATION_POSITION.TOP]);
    }
    /**
     * Show a new notification with the sent config
     * @param config
     */
    show(config) {
        if (this.parent === undefined)
            return;
        // Wrapper to remove notification
        const fnRemove = () => {
            if (this.parent === undefined)
                return;
            config.remove(this.parent[config.position]);
        };
        // Serialize title and body
        const t_title = serialize(config.title);
        const t_body = serialize(config.body);
        // Create base notification cell
        const cell = document.createElement('article');
        cell.setAttribute('id', config.id);
        cell.classList.add('f', 'fh', 'fa-s', 'pr', 'el-notification', `el--t-${config.type}`);
        // Add notification icon
        if (isNeString(config.getIcon())) {
            const cell_icon_wrapper = document.createElement('div');
            cell_icon_wrapper.classList.add('f', 'fv', 'fa-c', 'fj-c', 'fns', 'el-notification-icon');
            const cell_icon = document.createElement('i');
            cell_icon.classList.add('fns', 'fas-c', `lnr-${config.getIcon()}`);
            cell_icon_wrapper.appendChild(cell_icon);
            cell.appendChild(cell_icon_wrapper);
        }
        // Create notification content wrapper
        const cell_content = document.createElement('div');
        cell_content.classList.add('f', 'fh', 'fa-c', 'fj-c', 'fg', 'dw-10', 'sp-l', 'sp-r-xxl', 'el-notification-content');
        const cell_content_inner = document.createElement('div');
        cell_content_inner.classList.add('f', 'fv', 'fg');
        // Add title to content
        if (isNeString(t_title)) {
            const cell_content_title = document.createElement('h4');
            cell_content_title.classList.add('fg', 'sm-b-xs', 'te-fat-section');
            cell_content_title.appendChild(document.createTextNode(t_title));
            cell_content_inner.appendChild(cell_content_title);
        }
        // Add body to content
        if (isNeString(t_body)) {
            const cell_content_body = document.createElement('p');
            cell_content_body.appendChild(document.createTextNode(t_body));
            cell_content_inner.appendChild(cell_content_body);
        }
        cell_content.appendChild(cell_content_inner);
        // Add removal button
        const cell_btn = document.createElement('button');
        cell_btn.classList.add('el-btn-icon', 'el--s-s', 'el--transparent');
        cell_btn.addEventListener('click', fnRemove, true);
        const cell_btn_icon = document.createElement('i');
        cell_btn_icon.classList.add('lnr-cross');
        cell_btn.appendChild(cell_btn_icon);
        cell_content.appendChild(cell_btn);
        cell.appendChild(cell_content);
        //  Set timeout and add callback to config
        config.timeout = setTimeout(fnRemove, config.hide_after);
        //  Add to parent
        this.parent[config.position].insertAdjacentElement(config.position === NOTIFICATION_POSITION.TOP ? 'afterbegin' : 'beforeend', cell);
        // Animate in
        setTimeout(() => cell.classList.add('el--anim-in'), 200);
    }
    success(body, options = {}) {
        this.publish(NOTIFICATION_TYPE.SUCCESS, body, options);
    }
    warning(body, options = {}) {
        this.publish(NOTIFICATION_TYPE.WARNING, body, options);
    }
    error(body, options = {}) {
        this.publish(NOTIFICATION_TYPE.ERROR, body, options);
    }
    message(body, options = {}) {
        this.publish(NOTIFICATION_TYPE.DEFAULT, body, options);
    }
    //
    // Deprecated methods
    //
    /**
     * @deprecated use success
     */
    tsuccess(body, options = {}) {
        this.success(body, options);
    }
    /**
     * @deprecated use warning
     */
    twarning(body, options = {}) {
        this.warning(body, options);
    }
    /**
     * @deprecated use error
     */
    terror(body, options = {}) {
        this.error(body, options);
    }
    /**
     * @deprecated use show
     */
    publish(type, body, options = {}) {
        const config = new NotificationConfig()
            .setBody(body);
        if (isNeString(options.icon))
            config.setIcon(options.icon);
        if (isNeString(type))
            config.setType(type);
        if (isNeString(options.title))
            config.setTitle(options.title);
        if (isNeString(options.body))
            config.setBody(options.body);
        if (isNumber(options.hide_after))
            config.setHideTime(options.hide_after);
        if (isNeString(options.position))
            config.setPosition(options.position);
        else
            config.setPosition(NOTIFICATION_POSITION.TOP);
        this.show(config);
    }
}
const NotificationService = UINotificationService.getInstance();
export { UINotificationService, NotificationConfig, NotificationService, NOTIFICATION_POSITION, NOTIFICATION_TYPE };
export default NotificationService;
//# sourceMappingURL=Notification.js.map